import {ChatAgentContext, ChatMessage, ReadLandingPage, StreamEvent} from '@tinct/core-api-models';
import {ApiClient} from '../api-client/api-client';
import {ApiCollectionResponse, GetCollectionQueryParams} from '../types';
import {ApiError} from '../api-client/api-error';

export interface GetLandingPageChatAgentMessagesStaticFilter {
  landingPageId: string;
}

export interface GetLandingPagesStaticParams {
  organizationId: string;
}

export class LandingPagesEndpoint {
  constructor(private client: ApiClient) {}

  /** The workspace's landing pages (org id is a query param, not a path segment). */
  async getLandingPages(
    params: GetCollectionQueryParams<ReadLandingPage, GetLandingPagesStaticParams>,
  ): Promise<ApiCollectionResponse<ReadLandingPage>> {
    const { staticParams, fields, sort, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ReadLandingPage>(
      '/api/v1/landing-pages',
      fields,
      {
        offset,
        limit,
        sort,
        organizationId: staticParams?.organizationId,
        ...filters,
      },
    );
  }

  /**
   * The edit chat agent — the single edit path for hosted pages. Heartbeated
   * SSE stream (an edit is a silent 10–30s LLM call); the conversation is
   * created lazily on the first message. `context.blockId` is the selected
   * core block's id — the backend resolves it to the block's data-lp path
   * and composes the turn, so the client must not bake it into the message
   * text. Each applied edit emits a `data_update` for the new landing-page
   * version.
   */
  async postLandingPageMessageChat(
    landingPageId: string,
    message: string,
    context?: ChatAgentContext | null,
    onEvent?: (event: StreamEvent) => void,
    onError?: (error: ApiError) => void,
    onComplete?: () => void,
  ): Promise<void> {
    return this.client.postStream(
      `/api/v1/landing-pages/${landingPageId}/chat-agent`,
      {
        message,
        // JSON.stringify drops undefined — omitted entirely for a global ask.
        context: context ?? undefined,
      },
      onEvent,
      onError,
      onComplete,
    );
  }

  /** Paginated chat history (newest first); empty before the first turn. */
  async getLandingPageChatAgentMessages(
    params: GetCollectionQueryParams<ChatMessage, GetLandingPageChatAgentMessagesStaticFilter>,
  ): Promise<ApiCollectionResponse<ChatMessage>> {
    const { staticParams, fields, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ChatMessage>(
      `/api/v1/landing-pages/${staticParams?.landingPageId}/chat-agent/messages`,
      fields,
      {
        offset,
        limit,
        ...filters,
      },
    );
  }
}
